import React, {useEffect, useState} from 'react'
import {Navigate, useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import Swal from 'sweetalert2'
import {useAuth} from '../../../modules/auth'
import {shouldDisplayFonctionnalite} from '../../../../components/helpers'
import {LoadingData} from '../../../../components/LoadingData'
import {
  deleteSavLigne,
  derogerSavDossier,
  savErreur,
  transitionSavDossier,
  updateSavLigne,
} from '../../../../components/sav/requests'
import {formatSavMontant} from '../../../../components/sav/libelles'
import SavDossierHeader from '../components/SavDossierHeader'
import SavFilModifications from '../components/SavFilModifications'
import SavDocuments, {SavAjoutDocument} from '../components/SavDocuments'
import SavGarantie, {estSousGarantie} from '../components/SavGarantie'
import {useSavDossier} from '../components/useSavDossier'
import {useSavReferentiels} from '../components/useSavReferentiels'
import SavOrganiserRetour from './SavOrganiserRetour'
import {SavInformerClientModal} from '../components/SavInformerClient'

type Decision = 'refus' | 'accord' | 'retour'

// Écran 4 : qualification du dossier (refus, accord sans retour, organisation du retour)
const SavQualificationPage = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const referentiels = useSavReferentiels()
  const {detail, erreur, maj, messagePropose, fermerMessage} = useSavDossier(id)
  const [demandeReference, setDemandeReference] = useState(false)
  const [decision, setDecision] = useState<Decision | null>(null)
  const [motifRefus, setMotifRefus] = useState('')
  const [commentaire, setCommentaire] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const dossier = detail?.dossier
  const aRetourner = dossier?.lignes.some((l) => l.retourRequis) ?? false
  const horsGarantie = dossier?.lignes.filter((l) => !estSousGarantie(l)) ?? []
  const qualifier = shouldDisplayFonctionnalite(currentUser, 'SAV_QUALIFIER')

  // Décision proposée : retour si une pièce est à retourner, sinon accord sans retour
  useEffect(() => {
    if (dossier && !decision)
      setDecision(
        horsGarantie.length && !dossier.derogationGarantie
          ? 'refus'
          : aRetourner
          ? 'retour'
          : 'accord'
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossier?.id])

  if (erreur) return <div className='alert alert-danger'>{erreur}</div>
  if (!detail || !dossier || !referentiels) return <LoadingData />
  if (dossier.statut.code !== 'OUVERT')
    return <Navigate to={`/sav/dossiers/${dossier.id}/fiche`} replace />

  const basculerRetour = (ligneId: number, retourRequis: boolean) =>
    updateSavLigne(dossier.id, ligneId, {retourRequis})
      .then(maj)
      .catch((e) => toast.error(savErreur(e)))

  const supprimerLigne = (ligneId: number) =>
    deleteSavLigne(dossier.id, ligneId)
      .then(maj)
      .catch((e) => toast.error(savErreur(e)))

  const deroger = async () => {
    const {value, isConfirmed} = await Swal.fire({
      title: 'Accepter hors garantie',
      input: 'textarea',
      inputLabel: 'Commentaire (obligatoire)',
      showCancelButton: true,
      confirmButtonText: 'Accepter',
      cancelButtonText: 'Annuler',
      inputValidator: (v) => (!v?.trim() ? 'Le commentaire est obligatoire' : null),
    })
    if (!isConfirmed) return
    derogerSavDossier(dossier.id, value)
      .then((d) => {
        maj(d)
        toast.success('Dérogation enregistrée')
      })
      .catch((e) => toast.error(savErreur(e)))
  }

  const valider = () => {
    if (decision === 'retour') return
    setEnvoi(true)
    const appel =
      decision === 'refus'
        ? transitionSavDossier(dossier.id, 'CLOS_REFUSE', {
            motifRefus: Number(motifRefus),
            commentaire,
          })
        : transitionSavDossier(dossier.id, 'A_SOLDER', {commentaire})
    appel
      .then((d) => {
        toast.success(
          decision === 'refus' ? 'Dossier refusé' : 'Accord enregistré : dossier à solder'
        )
        // Le message proposé (refus motivé) s'ouvre sur l'écran suivant
        navigate(`/sav/dossiers/${dossier.id}/${decision === 'refus' ? 'fiche' : 'solution'}`, {
          state: {messagePropose: d.messagePropose},
        })
      })
      .catch((e) => toast.error(savErreur(e)))
      .finally(() => setEnvoi(false))
  }

  const annuler = async () => {
    const {value, isConfirmed} = await Swal.fire({
      title: 'Annuler le dossier ?',
      input: 'text',
      inputLabel: "Raison de l'annulation",
      showCancelButton: true,
      confirmButtonText: 'Annuler le dossier',
      cancelButtonText: 'Retour',
    })
    if (!isConfirmed) return
    transitionSavDossier(dossier.id, 'ANNULE', {commentaire: value})
      .then(() => navigate('/sav/liste'))
      .catch((e) => toast.error(savErreur(e)))
  }

  const decisions: Array<{
    cle: Decision
    titre: string
    description: string
    statut: string
    possible: boolean
    raison?: string
  }> = [
    {
      cle: 'refus',
      titre: 'Notifier un refus motivé',
      description: 'Hors garantie ou irrecevable',
      statut: 'Clos refusé',
      possible: detail.transitions.includes('CLOS_REFUSE'),
    },
    {
      cle: 'accord',
      titre: 'Accord sans retour',
      description: 'Erreur de facturation, pièce manquante',
      statut: 'À solder',
      possible: detail.transitions.includes('A_SOLDER') && !aRetourner,
      raison: aRetourner ? 'Des pièces sont à retourner.' : undefined,
    },
    {
      cle: 'retour',
      titre: 'Organiser le retour',
      description: 'Bon de retour et consignes au client',
      statut: 'Retour attendu',
      possible: detail.transitions.includes('RETOUR_ATTENDU') && aRetourner,
    },
  ]

  const voirVente =
    dossier.commande || dossier.catalogueCommande || dossier.clientHistoriqueFacturation

  return (
    <>
      <SavDossierHeader
        detail={detail}
        onRecharger={maj}
        ac10={{etape: 'Demande SAV', acteur: 'TC SAV', delai: 'T1'}}
        action={
          voirVente && (
            <button
              className='btn btn-sm btn-light-primary'
              onClick={() => navigate(`/sav/dossiers/${dossier.id}/fiche`)}
            >
              Voir la vente d'origine
            </button>
          )
        }
      />

      <div className='card mb-5'>
        <div className='card-header'>
          <h3 className='card-title'>Pièces réclamées</h3>
          {dossier.commande && (
            <div className='card-toolbar text-muted'>Commande {dossier.commande.numCommande}</div>
          )}
        </div>
        <div className='card-body'>
          <div className='table-responsive'>
            <table className='table align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th>Référence pièce</th>
                  <th>Immatriculation</th>
                  <th>Motif</th>
                  <th>Prix de vente HT</th>
                  <th>Garantie</th>
                  <th>Retour requis</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {dossier.lignes.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <span className='fw-bold'>
                        {(l.pieceEntre?.code ?? l.codePieceExterne) &&
                          `${l.pieceEntre?.code ?? l.codePieceExterne} - `}
                        {l.libellePiece}
                      </span>
                      {l.referenceConstructeur && (
                        <div className='text-muted fs-7'>Réf. OE {l.referenceConstructeur}</div>
                      )}
                    </td>
                    <td>{l.immatriculation ?? '—'}</td>
                    <td>{l.motif?.libelle}</td>
                    <td>
                      {formatSavMontant(l.prixVenteHt)}
                      <span
                        className={`ms-2 badge ${
                          l.prixVerifie ? 'badge-light-success' : 'badge-light-warning'
                        }`}
                      >
                        {l.prixVerifie ? 'vérifié' : 'déclaré'}
                      </span>
                    </td>
                    <td>
                      <SavGarantie ligne={l} declarative={detail.garantieDeclarative} />
                    </td>
                    <td>
                      <div className='form-check form-switch'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={l.retourRequis}
                          disabled={!detail.retourModifiable || !qualifier}
                          onChange={(e) => basculerRetour(l.id, e.target.checked)}
                        />
                      </div>
                    </td>
                    <td className='text-end'>
                      {dossier.lignes.length > 1 && qualifier && (
                        <button
                          className='btn btn-icon btn-sm btn-light'
                          title='Retirer la pièce'
                          onClick={() => supprimerLigne(l.id)}
                        >
                          <i className='bi bi-trash'></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className='row g-4 mt-2'>
            <div className='col-md-6'>
              <div className='bg-light rounded p-4 h-100'>
                <div className='fw-bold mb-2'>La pièce est-elle dans le délai de garantie ?</div>
                {horsGarantie.length === 0 ? (
                  <div className='text-success fw-bold'>
                    <i className='bi bi-check-circle-fill text-success me-2'></i>
                    Oui
                    {detail.garantieDeclarative &&
                      " (garantie déclarative : date d'achat non vérifiée)"}
                  </div>
                ) : dossier.derogationGarantie ? (
                  <div className='text-warning fw-bold'>Non · accepté par dérogation</div>
                ) : (
                  <>
                    <div className='text-danger fw-bold'>
                      Non : {horsGarantie.map((l) => l.libellePiece).join(', ')}
                    </div>
                    {shouldDisplayFonctionnalite(currentUser, 'SAV_DEROGER') && (
                      <button className='btn btn-sm btn-light-warning mt-3' onClick={deroger}>
                        Accepter par dérogation
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='bg-light rounded p-4 h-100'>
                <div className='fw-bold mb-2'>
                  Un retour physique de la pièce est-il nécessaire ?
                </div>
                <div className='fw-bold'>{aRetourner ? 'Oui' : 'Non'}</div>
                <div className='text-muted fs-7'>
                  Valeur par défaut du motif, modifiable par pièce ci-dessus.
                </div>
              </div>
            </div>
          </div>

          <div className='d-flex align-items-center gap-3 mt-5'>
            <span className='fw-bold'>Photos et pièces jointes</span>
            <button
              className='btn btn-sm btn-light-primary'
              onClick={() => setDemandeReference(true)}
            >
              <i className='bi bi-envelope'></i> Demander la référence au client
            </button>
            <SavAjoutDocument
              dossierId={dossier.id}
              type='PHOTO_CLIENT'
              libelle='Ajouter des photos'
              onAjoute={maj}
            />
            {dossier.origine === 'LIBRE' && (
              <SavAjoutDocument
                dossierId={dossier.id}
                type='JUSTIFICATIF'
                libelle='Ajouter un justificatif'
                onAjoute={maj}
              />
            )}
          </div>
          <div className='mt-3'>
            <SavDocuments documents={dossier.documents} />
          </div>
        </div>
      </div>

      {qualifier && (
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Décision de qualification</h3>
            {detail.transitions.includes('ANNULE') && (
              <div className='card-toolbar'>
                <button className='btn btn-sm btn-light-danger' onClick={annuler}>
                  Annuler le dossier
                </button>
              </div>
            )}
          </div>
          <div className='card-body'>
            <div className='row g-4 mb-5'>
              {decisions.map((d) => (
                <div className='col-md-4' key={d.cle}>
                  <div
                    className={`rounded p-4 h-100 ${d.possible ? 'cursor-pointer' : 'opacity-50'}`}
                    style={{
                      border: `2px solid ${decision === d.cle ? '#50CD89' : '#E1E3EA'}`,
                      backgroundColor: decision === d.cle ? '#E8FFF3' : '#FFFFFF',
                    }}
                    onClick={() => d.possible && setDecision(d.cle)}
                  >
                    <div className='fw-bold'>{d.titre}</div>
                    <div className='text-muted fs-7 my-1'>{d.description}</div>
                    <div className='fs-8'>→ {d.statut}</div>
                    {!d.possible && d.raison && (
                      <div className='fs-8 text-danger mt-1'>{d.raison}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {decision === 'refus' && (
              <div className='mb-4'>
                <label className='form-label required'>Motif du refus</label>
                <select
                  className='form-select'
                  value={motifRefus}
                  onChange={(e) => setMotifRefus(e.target.value)}
                >
                  <option value=''>Choisir un motif</option>
                  {referentiels.motifsRefus.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.libelle}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {decision === 'retour' ? (
              <SavOrganiserRetour
                detail={detail}
                referentiels={referentiels}
                onMaj={maj}
                onEnvoye={(d) =>
                  navigate(`/sav/dossiers/${dossier.id}/fiche`, {
                    state: {messagePropose: d.messagePropose},
                  })
                }
              />
            ) : (
              decision && (
                <>
                  <label className='form-label'>Commentaire</label>
                  <textarea
                    className='form-control form-control-solid mb-4'
                    rows={2}
                    value={commentaire}
                    onChange={(e) => setCommentaire(e.target.value)}
                  />
                  {decision === 'refus' && (
                    <div className='text-muted fs-7 mb-3'>
                      Le message « Refus motivé » vous sera proposé ensuite, modifiable avant
                      l'envoi.
                    </div>
                  )}
                  <button
                    className='btn btn-success'
                    disabled={envoi || (decision === 'refus' && !motifRefus)}
                    onClick={valider}
                  >
                    {decision === 'refus' ? 'Refuser le dossier' : "Valider l'accord sans retour"}
                  </button>
                </>
              )
            )}
          </div>
        </div>
      )}

      <SavFilModifications detail={detail} onRecharger={maj} />

      {messagePropose && (
        <SavInformerClientModal
          dossier={dossier}
          evenement={messagePropose}
          onClose={fermerMessage}
          onTermine={maj}
        />
      )}
      {demandeReference && (
        <SavInformerClientModal
          dossier={dossier}
          evenement='DEMANDE_REFERENCE'
          onClose={() => setDemandeReference(false)}
          onTermine={maj}
        />
      )}
    </>
  )
}

export default SavQualificationPage
